import React, { useState } from 'react';
import { Container, Card, Row, Form, Dropdown, Modal, Button, Spinner, Col, Accordion } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import '../styles/GetItinerary.css'

const ItemTypes = {
  CARD: 'card',
};

const createKey = (day, activity) => `${day}-${activity}`;

const DraggableCard = ({ item, index, moveCard, day, plan, onSuggestRestaurant, isEditing }) => {
  const [, ref] = useDrag({
    type: ItemTypes.CARD,
    item: { index, day },
    canDrag: isEditing,
  });

  const [, drop] = useDrop({
    accept: ItemTypes.CARD,
    hover: (draggedItem) => {
      if (draggedItem.index !== index || draggedItem.day !== day) {
        moveCard(draggedItem.index, index, draggedItem.day, day);
        draggedItem.index = index;
        draggedItem.day = day;
      }
    },
  });

  return (
    <Col md={12} ref={node => ref(drop(node))} style={{ marginBottom: '20px' }}>
      <Card>
        <Card.Body>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Card.Title style={{ textAlign: 'center', width: '100%' }}>{item.Activity}</Card.Title>
            {(item.Activity.includes('Lunch') || item.Activity.includes('Dinner')) && (
              <Dropdown align="end">
                <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic" />
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => onSuggestRestaurant(item, day, plan)}>
                    Suggest Different Restaurants
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
          <Card.Subtitle className="mb-2 text-muted">{item.Time}</Card.Subtitle>
          <Card.Text>{item['Activity Description']}</Card.Text>
          <Card.Footer>
            <small className="text-muted">{item.Location}</small>
          </Card.Footer>
        </Card.Body>
      </Card>
    </Col>
  );
};

const GetItinerary = () => {
  const location = useLocation();
  const { itinerary, selectedTier } = location.state || { itinerary: [], selectedTier: null };

  const [isEditing, setIsEditing] = useState(false);
  const [groupedItinerary, setGroupedItinerary] = useState(() => {
    return itinerary.reduce((acc, item) => {
      const day = item.Day;
      if (!acc[day]) {
        acc[day] = [];
      }
      acc[day].push(item);
      return acc;
    }, {});
  });

  const [suggestedRestaurants, setSuggestedRestaurants] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [currentDay, setCurrentDay] = useState(null);
  const [loading, setLoading] = useState(false);

  const moveCard = (draggedIndex, hoverIndex, draggedDay, hoverDay) => {
    const draggedItem = groupedItinerary[draggedDay][draggedIndex];
    groupedItinerary[draggedDay].splice(draggedIndex, 1);
    groupedItinerary[hoverDay].splice(hoverIndex, 0, draggedItem);
    setGroupedItinerary({ ...groupedItinerary });
  };

  const fetchRestaurantSuggestions = async (plan, location) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('plan', plan);
      formData.append('location', location);
      const response = await axios.post(
        'https://city-gateway-backend.vercel.app/api/restaurant/nearby',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return response.data.alternatives;
    } catch (error) {
      console.error('Error fetching restaurant suggestions:', error.response ? error.response.data : error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const onSuggestRestaurant = async (item, day, plan) => {
    const key = createKey(day, item.Activity);
    console.log(suggestedRestaurants[key]);
    if (suggestedRestaurants[key]) {
      setCurrentCard(item);
      setCurrentDay(day);
      setShowSuggestions(true);
    } else {
      const newSuggestions = await fetchRestaurantSuggestions(plan, item.Location);
      const suggestionsWithActivity = newSuggestions.map(restaurant => ({
        ...restaurant,
        Activity: item.Activity,
        'Activity Description': item['Activity Description'],
      }));

      setSuggestedRestaurants(prev => ({
        ...prev,
        [key]: [item, ...suggestionsWithActivity],
      }));

      setCurrentCard(item);
      setCurrentDay(day);
      setShowSuggestions(true);
    }
  };

  const onSelectRestaurant = (newRestaurant) => {
    const updatedDay = groupedItinerary[currentDay].map(card => {
      if (card.Activity === currentCard.Activity && card.Location === currentCard.Location) {
        return {
          ...card,
          Location: newRestaurant.name || newRestaurant.Location,
          'Activity Description': newRestaurant.description || newRestaurant['Activity Description'],
        };
      }
      return card;
    });

    setGroupedItinerary(prev => ({
      ...prev,
      [currentDay]: updatedDay,
    }));

    setShowSuggestions(false);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="get-itinerary-page">
        <Form.Check
          type="switch"
          id="edit-switch"
          label={isEditing ? 'Finish Editing' : 'Edit Itinerary'}
          checked={isEditing}
          onChange={() => setIsEditing(!isEditing)}
          className="ml-auto"
          style={{ marginLeft: 'auto' }}
        />
        
        <Container>
          <h2 className="text-center mb-4" style={{ padding: '25px' }}>Your Personalized Travel Itinerary</h2>
          {Object.keys(groupedItinerary).map(day => (
            <div key={day} style={{ marginBottom: '40px' }}>
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey={day}>
                  <Accordion.Header style={{fontWeight:'bold'}}>Day {day}</Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      {groupedItinerary[day].map((item, index) => (
                        <DraggableCard
                          key={index}
                          item={item}
                          index={index}
                          day={day}
                          plan={selectedTier}
                          moveCard={moveCard}
                          isEditing={isEditing}
                          onSuggestRestaurant={onSuggestRestaurant}
                        />
                      ))}
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          ))}
        </Container>
        
        <Modal show={showSuggestions} onHide={() => setShowSuggestions(false)} className="modal-lg">
          <Modal.Header closeButton>
            <Modal.Title>Other restaurants to consider</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" />
                <p>Loading suggestions...</p>
              </div>
            ) : (
              <Row>
                {suggestedRestaurants[createKey(currentDay, currentCard?.Activity)]?.filter(
                  restaurant => (restaurant.name || restaurant.Location) !== currentCard.Location
                ).map((restaurant, idx) => (
                  <Col key={idx} md={12} className="mb-4">
                    <Card style={{ margin: 'auto', padding: '10px' }}>
                      <Card.Body>
                        <Card.Title>{restaurant.name || restaurant.Location}</Card.Title>
                        <Card.Text>{restaurant.description || restaurant['Activity Description']}</Card.Text>
                        <Button variant="primary" onClick={() => onSelectRestaurant(restaurant)}>
                          Select
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </DndProvider>
  );
};

export default GetItinerary;